import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Home } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const Privacy = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{t('privacy.title')} - Spawncore</title>
        <meta name="description" content="Política de proteção de dados e privacidade da Spawncore. Informação sobre como tratamos e protegemos os seus dados pessoais de acordo com o RGPD." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://spawncore.com/privacy" />
      </Helmet>
      
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/" className="flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  {t('nav.home')}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t('privacy.breadcrumb')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="fade-in">
          <h1 className="text-4xl font-bold text-foreground mb-8">
            {t('privacy.title')}
          </h1>
          
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-8">
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">1. Responsável pelo Tratamento</h2>
              <p>
                A Spawncore, com sede no Largo do Esteiro 6, 2050-261 Azambuja, Portugal, 
                é a entidade responsável pelo tratamento dos seus dados pessoais.
              </p>
              <div className="bg-secondary p-6 rounded-lg">
                <p><strong>Contacto:</strong> joao@spawncore.com</p>
                <p><strong>Morada:</strong> Largo do Esteiro 6, 2050-261 Azambuja</p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">2. Dados Pessoais Recolhidos</h2>
              <p>Recolhemos os seguintes tipos de dados pessoais:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Dados de identificação:</strong> Nome, endereço de email</li>
                <li><strong>Dados empresariais:</strong> Nome da empresa, cargo</li>
                <li><strong>Dados de comunicação:</strong> Mensagens enviadas através dos nossos formulários de contacto</li>
                <li><strong>Dados técnicos:</strong> Endereço IP, tipo de navegador, dados de navegação (cookies)</li>
                <li><strong>Dados de interação:</strong> Informações sobre submissões de formulários, conversões e interações com o website através do Google Analytics e Google AdSense</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">3. Finalidades e Base Legal do Tratamento</h2>
              <div className="space-y-4">
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Prestação de Serviços</h4>
                  <p><strong>Finalidade:</strong> Responder a pedidos de informação e fornecer os nossos serviços</p>
                  <p><strong>Base legal:</strong> Execução de contrato (Art. 6.º, n.º 1, al. b) do RGPD)</p>
                </div>
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Marketing Direto</h4>
                  <p><strong>Finalidade:</strong> Envio de informações sobre serviços e novidades</p>
                  <p><strong>Base legal:</strong> Consentimento (Art. 6.º, n.º 1, al. a) do RGPD)</p>
                </div>
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Análise e Melhoria do Website</h4>
                  <p><strong>Finalidade:</strong> Análise de tráfego e otimização da experiência do utilizador</p>
                  <p><strong>Base legal:</strong> Interesse legítimo (Art. 6.º, n.º 1, al. f) do RGPD)</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">4. Prazo de Conservação</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Dados de contacto e comunicação:</strong> 3 anos após o último contacto</li>
                <li><strong>Dados de execução de contratos:</strong> 5 anos após o término do contrato</li>
                <li><strong>Dados de marketing:</strong> Até retirada do consentimento ou 2 anos sem interação</li>
                <li><strong>Cookies e dados técnicos:</strong> Conforme configurações do navegador (máximo 12 meses)</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">5. Os Seus Direitos</h2>
              <p>Ao abrigo do RGPD, tem os seguintes direitos:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Direito de Acesso</h4>
                  <p>Pode solicitar informações sobre os dados que processamos sobre si</p>
                </div>
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Direito de Retificação</h4>
                  <p>Pode solicitar a correção de dados incorretos ou incompletos</p>
                </div>
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Direito ao Apagamento</h4>
                  <p>Pode solicitar a eliminação dos seus dados pessoais</p>
                </div>
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Direito à Limitação</h4>
                  <p>Pode solicitar a limitação do tratamento dos seus dados</p>
                </div>
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Direito à Portabilidade</h4>
                  <p>Pode solicitar os seus dados num formato estruturado</p>
                </div>
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Direito de Oposição</h4>
                  <p>Pode opor-se ao tratamento dos seus dados pessoais</p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">6. Partilha de Dados</h2>
              <p>
                Os seus dados pessoais podem ser partilhados com:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Prestadores de serviços técnicos:</strong> Para alojamento web e manutenção do website</li>
                <li><strong>Resend:</strong> Serviço de envio de emails utilizado para processar e enviar mensagens do formulário de contacto</li>
                <li><strong>Google Analytics:</strong> Para análise de tráfego e comportamento dos utilizadores no website</li>
                <li><strong>Google AdSense:</strong> Para rastreamento de conversões publicitárias e otimização de campanhas</li>
                <li><strong>Autoridades competentes:</strong> Quando exigido por lei</li>
              </ul>
              <p>
                <strong>Transferências internacionais:</strong> Alguns dos nossos prestadores de serviços (Resend, Google Analytics, Google AdSense) podem estar 
                localizados fora da UE. Nestes casos, garantimos que existem garantias adequadas de proteção 
                dos dados conforme o RGPD, incluindo cláusulas contratuais padrão aprovadas pela Comissão Europeia.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">7. Cookies e Tecnologias de Rastreamento</h2>
              <p>
                O nosso website utiliza cookies e outras tecnologias de rastreamento para melhorar a sua experiência de navegação, 
                analisar o tráfego do website e medir a eficácia das nossas campanhas publicitárias. 
                Pode gerir as suas preferências de cookies nas definições do seu navegador.
              </p>
              <div className="space-y-4">
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Tipos de Cookies Utilizados:</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Cookies essenciais:</strong> Necessários para o funcionamento do website e gestão de consentimentos</li>
                    <li><strong>Google Analytics:</strong> Cookies analíticos utilizados para análise de tráfego, comportamento dos utilizadores e estatísticas do website</li>
                    <li><strong>Google AdSense:</strong> Cookies de conversão e rastreamento publicitário utilizados para medir a eficácia das campanhas e otimizar anúncios (ID de conversão: AW-17675770920)</li>
                  </ul>
                </div>
                <div className="bg-secondary p-4 rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Rastreamento de Conversões:</h4>
                  <p>
                    Utilizamos o Google AdSense para rastrear conversões quando submete o formulário de contacto. 
                    Esta informação permite-nos medir a eficácia das nossas campanhas publicitárias. 
                    O rastreamento é feito de forma anónima e não permite identificá-lo pessoalmente através dos anúncios.
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">8. Segurança dos Dados</h2>
              <p>
                Implementamos medidas técnicas e organizacionais adequadas para proteger os seus dados pessoais 
                contra acesso não autorizado, alteração, divulgação ou destruição, incluindo:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encriptação de dados em trânsito e em repouso</li>
                <li>Controlo de acesso restrito aos dados</li>
                <li>Formação regular da equipa sobre proteção de dados</li>
                <li>Auditorias regulares de segurança</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">9. Exercício de Direitos e Contactos</h2>
              <p>
                Para exercer qualquer dos seus direitos ou esclarecer dúvidas sobre esta política, 
                pode contactar-nos através de:
              </p>
              <div className="bg-secondary p-6 rounded-lg">
                <p><strong>Email:</strong> joao@spawncore.com</p>
                <p><strong>Morada:</strong> Largo do Esteiro 6, 2050-261 Azambuja</p>
                <p><strong>Assunto:</strong> "Proteção de Dados - [indicar o direito que pretende exercer]"</p>
              </div>
              <p>
                Responderemos ao seu pedido no prazo máximo de 30 dias, conforme estabelecido no RGPD.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">10. Direito de Reclamação</h2>
              <p>
                Tem o direito de apresentar reclamação junto da autoridade de controlo competente se considerar 
                que o tratamento dos seus dados pessoais viola o RGPD.
              </p>
              <div className="bg-secondary p-6 rounded-lg">
                <p><strong>Autoridade Portuguesa:</strong></p>
                <p>Comissão Nacional de Proteção de Dados (CNPD)</p>
                <p>Website: <a href="https://www.cnpd.pt" className="text-primary hover:text-primary-light">www.cnpd.pt</a></p>
                <p>Email: geral@cnpd.pt</p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">11. Alterações a Esta Política</h2>
              <p>
                Esta política de privacidade pode ser atualizada periodicamente para refletir alterações 
                nas nossas práticas ou na legislação aplicável. A versão mais recente estará sempre 
                disponível nesta página.
              </p>
              <p>
                Recomendamos que consulte regularmente esta página para se manter informado sobre como 
                protegemos os seus dados pessoais.
              </p>
            </section>

            <div className="text-sm text-muted-foreground mt-12 pt-8 border-t border-border">
              <p><strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-PT', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
              <p><strong>Versão:</strong> 2.0 - Conforme RGPD</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;